import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  FlagRounded,
  RestartAltRounded,
  ZoomInRounded,
  ZoomOutRounded,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enGB from 'date-fns/locale/en-GB';
import { addDays, format, isValid, parseISO } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { dateLocale } from 'i18n';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { listDocuments } from 'services/patientDocumentsService';
import ConfirmDialog from 'components/common/ConfirmDialog';

/**
 * A strip timeline of when the patient's uploaded documents are from, so the
 * record reads as a history rather than a pile of files. Milestones (flag
 * button, top right) let the patient pin their own annotations — "surgery",
 * "moved abroad" — on the same time axis.
 *
 * Documents carry no extracted content date, so the date shown is taken from
 * the filename when it contains one (yyyy-mm-dd), and falls back to the upload
 * date otherwise.
 */

const FILENAME_DATE = /\d{4}-\d{2}-\d{2}/;

function documentDate(doc) {
  const match = FILENAME_DATE.exec(doc.fileName || '');
  if (match) {
    const parsed = parseISO(match[0]);
    if (isValid(parsed)) return parsed;
  }
  return doc.createdAt ? new Date(doc.createdAt) : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const CHART_HEIGHT = 150;
const MARGIN_X = 28;
const BASELINE_Y = 96;
const TICK_COUNT = 4;
const FONT_SIZE = 13;
// Milestone labels alternate between two rows so neighbours don't collide.
const FLAG_ROWS = [34, 58];
// Medication courses sit in lanes below the date labels. Each lane holds a
// name above a bar; courses that overlap in time get separate lanes.
const MED_TOP = BASELINE_Y + 40;
const MED_LANE_HEIGHT = 26;
const MED_BAR_HEIGHT = 8;
/** Rough width of one label character at FONT_SIZE, for truncating names to
 *  the bar they sit on. */
const LABEL_CHAR_PX = 7;
/** A bar narrower than this has no room for a readable name; its tooltip
 *  still names it. */
const MIN_LABEL_CHARS = 4;
/** Two days: past this, zooming further reveals nothing new on a day axis. */
const MIN_ZOOM_SPAN_MS = 2 * DAY_MS;
const ZOOM_STEP = 1.6;
/** Wheel delta → zoom factor. One mouse-wheel notch (~100) zooms about 1.2×,
 *  and a trackpad's stream of small deltas zooms smoothly instead of in jumps. */
const WHEEL_ZOOM_SENSITIVITY = 0.002;
/** Movement before a press counts as a drag, so a click on a flag still lands. */
const DRAG_THRESHOLD_PX = 4;
/** Release speed is measured over the tail of the drag only. */
const VELOCITY_WINDOW_MS = 100;
/** Share of glide speed kept per 60fps frame. */
const MOMENTUM_FRICTION = 0.94;
/** Below this (px/ms) the glide is imperceptible, so it stops. */
const MOMENTUM_MIN_VELOCITY = 0.02;
/** Zoom eases toward its target with this time constant: ~95% of the way
 *  there after three of them, so a notch settles in about a quarter second. */
const ZOOM_EASE_MS = 80;

const truncate = (s, max) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/**
 * Medications with both a start and an end date, as courses laid out in lanes.
 * A course runs to the END of its last day, so a one-day course still has a
 * width. Lanes are assigned in time, not pixels, so zooming never reshuffles
 * which row a medication sits in.
 */
function buildCourses(medications) {
  const courses = (medications ?? [])
    .map((med) => {
      const start = med?.startDate ? parseISO(med.startDate) : null;
      const end = med?.endDate ? parseISO(med.endDate) : null;
      if (!isValid(start) || !isValid(end) || end < start) return null;
      return {
        id: med.id,
        // null: the fallback name is filled in at render, in the current language.
        name: med.name || null,
        start,
        end,
        startT: start.getTime(),
        endT: addDays(end, 1).getTime(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startT - b.startT);

  const laneEnds = [];
  for (const course of courses) {
    let lane = laneEnds.findIndex((endT) => endT <= course.startT);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = course.endT;
    course.lane = lane;
  }
  return { courses, laneCount: laneEnds.length };
}

/**
 * Fit a requested time window inside the data's extent. Returns null when the
 * window covers everything, which is the "fitted" view.
 */
function clampView(start, end, fullMin, fullMax) {
  const span = end - start;
  if (span >= fullMax - fullMin) return null;
  let s = Math.max(start, fullMin);
  if (s + span > fullMax) s = fullMax - span;
  return { start: s, end: s + span };
}

/** Track the rendered width so the SVG uses real pixels (no stretched text). */
function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const DocumentsTimeline = () => {
  const theme = useTheme();
  const { t } = useTranslation('masterchart');
  const locale = dateLocale();
  // PP reads "Mar 1, 2026" in English and "1 mar 2026" in Spanish.
  const formatDay = (date) => format(date, 'PP', { locale });
  const { user, userData, medications } = useContext(AuthContext);

  const [containerRef, width] = useContainerWidth();
  const [documents, setDocuments] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Milestone being edited; null while the dialog is adding a new one.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // null = fitted to everything; otherwise the visible time window (ms).
  const [view, setView] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Held in state, not a ref, so the wheel listener attaches whenever the SVG
  // actually mounts — it renders only after both documents and width arrive.
  const [svgEl, setSvgEl] = useState(null);
  // Interaction math reads these refs rather than render state: wheel and
  // pointer events arrive faster than React re-renders, and zooming from a
  // stale view makes consecutive notches fight each other.
  const viewRef = useRef(null);
  const extentRef = useRef({ fullMin: 0, fullMax: 0, innerWidth: 1 });
  const dragRef = useRef(null);
  const momentumRef = useRef(null);
  // Where an in-flight zoom animation is heading ({start, end}, or null for
  // the fitted view), and its frame handle.
  const zoomTargetRef = useRef(null);
  const zoomAnimRef = useRef(null);
  // Set when a press turned into a drag, so releasing over a flag doesn't
  // also open its edit dialog.
  const suppressClickRef = useRef(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { date: null, text: '' },
  });

  useEffect(() => {
    let cancelled = false;
    listDocuments()
      .then(({ documents: docs }) => {
        if (!cancelled) setDocuments(docs);
      })
      .catch(() => {
        if (!cancelled) {
          setDocuments([]);
          setError('timeline.loadError');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live via the users/{uid} snapshot in AuthContext: a saved milestone shows
  // up here without any local mirroring.
  const milestones = useMemo(
    () =>
      (userData?.healthRecords?.milestones ?? [])
        .filter((m) => m?.date && isValid(parseISO(m.date)))
        .map((m) => ({ ...m, parsedDate: parseISO(m.date) }))
        .sort((a, b) => a.parsedDate - b.parsedDate),
    [userData?.healthRecords?.milestones]
  );

  // Live too: medications come from their own AuthContext snapshot.
  const { courses, laneCount } = useMemo(
    () => buildCourses(medications),
    [medications]
  );

  const docPoints = useMemo(
    () =>
      (documents ?? [])
        .map((doc) => ({ ...doc, parsedDate: documentDate(doc) }))
        .filter((doc) => doc.parsedDate)
        .sort((a, b) => a.parsedDate - b.parsedDate),
    [documents]
  );

  const saveMilestones = async (next) => {
    // merge:true keeps the rest of healthRecords; the array itself is replaced.
    await db
      .collection('users')
      .doc(user.uid)
      .set({ healthRecords: { milestones: next } }, { merge: true });
  };

  const openAddDialog = () => {
    setEditing(null);
    reset({ date: null, text: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (milestone) => {
    setEditing(milestone);
    reset({ date: milestone.parsedDate, text: milestone.text });
    setDialogOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    setError(null);
    try {
      const stored = userData?.healthRecords?.milestones ?? [];
      const saved = {
        // format, not toISOString: a milestone is a calendar day, not an instant.
        date: format(values.date, 'yyyy-MM-dd'),
        text: values.text.trim(),
      };
      const next = editing
        ? stored.map((m) => (m.id === editing.id ? { ...m, ...saved } : m))
        : [
            ...stored,
            { id: crypto.randomUUID?.() ?? String(Date.now()), ...saved },
          ];
      await saveMilestones(next);
      setDialogOpen(false);
      setEditing(null);
      reset({ date: null, text: '' });
    } catch {
      setError('timeline.saveError');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      const next = (userData?.healthRecords?.milestones ?? []).filter(
        (m) => m.id !== pendingDelete.id
      );
      await saveMilestones(next);
      setPendingDelete(null);
      // The edit panel the removal came from has nothing left to edit.
      setDialogOpen(false);
      setEditing(null);
    } catch {
      setError('timeline.removeError');
    } finally {
      setDeleting(false);
    }
  };

  // ── Scale & zoom ─────────────────────────────────────────────────────────
  const allTimes = [
    ...[...docPoints, ...milestones].map((p) => p.parsedDate.getTime()),
    ...courses.flatMap((c) => [c.startT, c.endT]),
  ];
  const hasPoints = allTimes.length > 0;
  const rawMin = hasPoints ? Math.min(...allTimes) : 0;
  const rawMax = hasPoints ? Math.max(...allTimes) : 0;
  // A lone point (or same-day points) still needs a readable axis around it.
  const pad = Math.max((rawMax - rawMin) * 0.06, 15 * DAY_MS);
  const fullMin = rawMin - pad;
  const fullMax = rawMax + pad;
  const domainStart = view?.start ?? fullMin;
  const domainEnd = view?.end ?? fullMax;
  const innerWidth = Math.max(width - MARGIN_X * 2, 0);
  const x = (t) =>
    MARGIN_X + ((t - domainStart) / (domainEnd - domainStart)) * innerWidth;
  extentRef.current = { fullMin, fullMax, innerWidth: Math.max(innerWidth, 1) };

  // Stable: everything inside reads refs, so the listeners registered below
  // never need re-binding and never act on a stale view.
  const interaction = useMemo(() => {
    const commit = (next) => {
      viewRef.current = next;
      setView(next);
      return next;
    };
    const domain = () => {
      const { fullMin: lo, fullMax: hi } = extentRef.current;
      const v = viewRef.current;
      return v ? [v.start, v.end] : [lo, hi];
    };
    const stopMomentum = () => {
      if (momentumRef.current) cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    };
    const stopZoom = () => {
      if (zoomAnimRef.current) cancelAnimationFrame(zoomAnimRef.current);
      zoomAnimRef.current = null;
    };
    const stopAll = () => {
      stopMomentum();
      stopZoom();
    };

    /** Ease the visible window toward `target` over a few frames. Calling it
     *  mid-flight just moves the destination; the glide carries on from
     *  wherever it currently is. */
    const animateTo = (target) => {
      stopMomentum();
      zoomTargetRef.current = target;
      if (zoomAnimRef.current) return;
      let prev = performance.now();
      const step = (now) => {
        const dt = Math.min(Math.max(now - prev, 0), 50);
        prev = now;
        const { fullMin: lo, fullMax: hi } = extentRef.current;
        const goal = zoomTargetRef.current;
        const [goalStart, goalEnd] = goal ? [goal.start, goal.end] : [lo, hi];
        const [s, e] = domain();
        // Close enough that the remaining distance is sub-pixel: land exactly.
        if (
          Math.abs(goalStart - s) + Math.abs(goalEnd - e) <
          (goalEnd - goalStart) * 0.002
        ) {
          commit(goal);
          zoomAnimRef.current = null;
          return;
        }
        // Frame-rate independent exponential ease. Both edges move by the same
        // fraction, so the date under the cursor stays put while zooming.
        const alpha = 1 - Math.exp(-dt / ZOOM_EASE_MS);
        commit({
          start: s + (goalStart - s) * alpha,
          end: e + (goalEnd - e) * alpha,
        });
        zoomAnimRef.current = requestAnimationFrame(step);
      };
      zoomAnimRef.current = requestAnimationFrame(step);
    };

    const zoomAt = (factor, centerT) => {
      const { fullMin: lo, fullMax: hi } = extentRef.current;
      // Consecutive notches compound on the destination, not on the frame
      // currently showing, so a fast scroll zooms as far as it should.
      const pending = zoomAnimRef.current ? zoomTargetRef.current : viewRef.current;
      const baseSpan = pending ? pending.end - pending.start : hi - lo;
      const newSpan = Math.max(baseSpan / factor, MIN_ZOOM_SPAN_MS);
      // Keep centerT at the screen position it occupies right now.
      const [s, e] = domain();
      const ratio = (centerT - s) / (e - s);
      animateTo(
        clampView(centerT - newSpan * ratio, centerT + newSpan * (1 - ratio), lo, hi)
      );
    };
    /** Pan by a pixel distance; positive moves later in time. */
    const panByPx = (dx) => {
      const [s, e] = domain();
      const { fullMin: lo, fullMax: hi, innerWidth: w } = extentRef.current;
      const dt = (dx / w) * (e - s);
      return commit(clampView(s + dt, e + dt, lo, hi));
    };
    /** Keep gliding after a flick, slowing each frame until it's imperceptible. */
    const startMomentum = (velocity) => {
      stopMomentum();
      let v = velocity;
      let prev = performance.now();
      const step = (now) => {
        const dt = Math.min(Math.max(now - prev, 0), 50);
        prev = now;
        const next = panByPx(v * dt);
        v *= MOMENTUM_FRICTION ** (dt / 16.67);
        const { fullMin: lo, fullMax: hi } = extentRef.current;
        // Stop at either end of the data rather than pressing against it.
        const atEdge = !next || next.start <= lo || next.end >= hi - 1;
        if (Math.abs(v) < MOMENTUM_MIN_VELOCITY || atEdge) {
          momentumRef.current = null;
          return;
        }
        momentumRef.current = requestAnimationFrame(step);
      };
      momentumRef.current = requestAnimationFrame(step);
    };
    const reset = () => animateTo(null);
    return { domain, zoomAt, panByPx, startMomentum, stopMomentum, stopAll, reset };
  }, []);

  useEffect(() => interaction.stopAll, [interaction]);

  // Wheel zoom needs a non-passive listener: React registers onWheel passive,
  // so preventDefault (to keep the page from scrolling) is ignored there.
  useEffect(() => {
    if (!svgEl) return undefined;
    const onWheel = (e) => {
      // Line-mode wheels (Firefox) report rows, not pixels.
      const delta = e.deltaY * (e.deltaMode === 1 ? 16 : 1);
      // Already fully zoomed out, and not mid-way into a zoom: scrolling down
      // should scroll the page.
      if (delta > 0 && !viewRef.current && !zoomAnimRef.current) return;
      e.preventDefault();
      interaction.stopMomentum();
      const rect = svgEl.getBoundingClientRect();
      const [s, end] = interaction.domain();
      const { innerWidth: w } = extentRef.current;
      const centerT = s + ((e.clientX - rect.left - MARGIN_X) / w) * (end - s);
      interaction.zoomAt(Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY), centerT);
    };
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, [svgEl, interaction]);

  const handlePointerDown = (e) => {
    // Grabbing the chart freezes whatever it was doing, glide or zoom alike.
    interaction.stopAll();
    suppressClickRef.current = false;
    // Nothing to pan to when everything already fits.
    if (!viewRef.current || e.button !== 0) return;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      lastX: e.clientX,
      active: false,
      samples: [{ x: e.clientX, t: e.timeStamp }],
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (!drag.active) {
      if (Math.abs(e.clientX - drag.startX) < DRAG_THRESHOLD_PX) return;
      // Captured only once it's really a drag; capturing on press would
      // retarget the click away from the flag under the pointer.
      drag.active = true;
      suppressClickRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    // Dragging right reveals earlier dates, like grabbing a sheet of paper.
    interaction.panByPx(drag.lastX - e.clientX);
    drag.lastX = e.clientX;
    drag.samples.push({ x: e.clientX, t: e.timeStamp });
    while (
      drag.samples.length > 2 &&
      e.timeStamp - drag.samples[0].t > VELOCITY_WINDOW_MS
    ) {
      drag.samples.shift();
    }
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag?.active) return;
    setDragging(false);
    if (e.type === 'pointercancel') return;
    const first = drag.samples[0];
    const last = drag.samples[drag.samples.length - 1];
    const elapsed = last.t - first.t;
    // Holding still before letting go means "stop here", not "throw".
    if (elapsed <= 0 || e.timeStamp - last.t > VELOCITY_WINDOW_MS) return;
    interaction.startMomentum((first.x - last.x) / elapsed);
  };

  const inDomain = (p) => {
    const t = p.parsedDate.getTime();
    return t >= domainStart && t <= domainEnd;
  };

  const spanDays = (domainEnd - domainStart) / DAY_MS;
  // Patterns from the locale file: Spanish puts the day first.
  const tickFormat =
    spanDays > 300
      ? t('timeline.tickFormatMonth')
      : spanDays > 45
        ? 'PP'
        : t('timeline.tickFormatDay');
  const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
    const tick = domainStart + ((domainEnd - domainStart) * i) / (TICK_COUNT - 1);
    return { t: tick, label: format(new Date(tick), tickFormat, { locale }) };
  });

  const loading = documents === null;
  const chartHeight = laneCount
    ? MED_TOP + laneCount * MED_LANE_HEIGHT
    : CHART_HEIGHT;
  const plotLeft = MARGIN_X;
  const plotRight = width - MARGIN_X;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Typography variant="subtitle2">{t('timeline.title')}</Typography>
        <Stack direction="row" spacing={0.5}>
          {hasPoints && (
            <>
              {view && (
                <Tooltip title={t('timeline.resetZoom')}>
                  <IconButton size="small" onClick={interaction.reset}>
                    <RestartAltRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('timeline.zoomOut')}>
                <IconButton
                  size="small"
                  onClick={() =>
                    interaction.zoomAt(1 / ZOOM_STEP, (domainStart + domainEnd) / 2)
                  }
                >
                  <ZoomOutRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('timeline.zoomIn')}>
                <IconButton
                  size="small"
                  onClick={() =>
                    interaction.zoomAt(ZOOM_STEP, (domainStart + domainEnd) / 2)
                  }
                >
                  <ZoomInRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title={t('timeline.addMilestone')}>
            <IconButton size="small" onClick={openAddDialog}>
              <FlagRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>
          {t(error)}
        </Alert>
      )}

      <Box ref={containerRef} sx={{ mt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !hasPoints ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 2 }}>
            {t('timeline.empty')}
          </Typography>
        ) : width > 0 ? (
          <svg
            ref={setSvgEl}
            width={width}
            height={chartHeight}
            role="img"
            aria-label={t('timeline.ariaLabel')}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              display: 'block',
              touchAction: 'none',
              // Labels are chart furniture; selecting them mid-drag is noise.
              userSelect: 'none',
              WebkitUserSelect: 'none',
              cursor: dragging ? 'grabbing' : view ? 'grab' : 'default',
            }}
          >
            {/* Axis */}
            <line
              x1={MARGIN_X}
              x2={width - MARGIN_X}
              y1={BASELINE_Y}
              y2={BASELINE_Y}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            {ticks.map(({ t, label }) => (
              <g key={t}>
                <line
                  x1={x(t)}
                  x2={x(t)}
                  y1={BASELINE_Y}
                  y2={BASELINE_Y + 4}
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                />
                <text
                  x={x(t)}
                  y={BASELINE_Y + 22}
                  textAnchor="middle"
                  fontSize={FONT_SIZE}
                  fill={theme.palette.text.secondary}
                >
                  {label}
                </text>
              </g>
            ))}

            {/* Milestones — annotation flags above the axis */}
            {milestones.filter(inDomain).map((m, i) => {
              const px = x(m.parsedDate.getTime());
              const rowY = FLAG_ROWS[i % FLAG_ROWS.length];
              return (
                <Tooltip
                  key={m.id}
                  title={t('timeline.milestoneTooltip', {
                    text: m.text,
                    date: formatDay(m.parsedDate),
                  })}
                >
                  <g
                    onClick={() => {
                      if (!suppressClickRef.current) openEditDialog(m);
                    }}
                    style={{ cursor: dragging ? 'grabbing' : 'pointer' }}
                  >
                    <line
                      x1={px}
                      x2={px}
                      y1={BASELINE_Y - 4}
                      y2={rowY}
                      stroke={theme.palette.text.secondary}
                      strokeWidth={1.5}
                    />
                    <path
                      d={`M ${px} ${rowY} l 11 4 l -11 4 Z`}
                      fill={theme.palette.text.primary}
                    />
                    <text
                      x={px + 15}
                      y={rowY + 9}
                      fontSize={FONT_SIZE}
                      fill={theme.palette.text.primary}
                    >
                      {truncate(m.text, 26)}
                    </text>
                  </g>
                </Tooltip>
              );
            })}

            {/* Medication courses — only those with both a start and an end */}
            {courses
              .filter((c) => c.startT <= domainEnd && c.endT >= domainStart)
              .map((course) => {
                // Clip to the plot so a course running off either edge stops
                // at the margin instead of drawing over it.
                const x1 = Math.max(x(course.startT), plotLeft);
                const x2 = Math.min(x(course.endT), plotRight);
                // Very short courses stay visible when zoomed far out.
                const barWidth = Math.max(x2 - x1, 4);
                const laneTop = MED_TOP + course.lane * MED_LANE_HEIGHT;
                const labelChars = Math.floor(barWidth / LABEL_CHAR_PX);
                const courseName = course.name || t('timeline.medicationFallback');
                return (
                  <Tooltip
                    key={course.id}
                    title={t('timeline.courseTooltip', {
                      name: courseName,
                      start: formatDay(course.start),
                      end: formatDay(course.end),
                    })}
                  >
                    <g>
                      {labelChars >= MIN_LABEL_CHARS && (
                        <text
                          x={x1}
                          y={laneTop + 10}
                          fontSize={FONT_SIZE}
                          fill={theme.palette.text.primary}
                        >
                          {truncate(courseName, labelChars)}
                        </text>
                      )}
                      <rect
                        x={x1}
                        y={laneTop + 14}
                        width={barWidth}
                        height={MED_BAR_HEIGHT}
                        rx={MED_BAR_HEIGHT / 2}
                        // Matches the green the record hub uses for Medications.
                        fill={theme.palette.success.main}
                      />
                    </g>
                  </Tooltip>
                );
              })}

            {/* Documents */}
            {docPoints.filter(inDomain).map((doc) => (
              <Tooltip
                key={doc.id}
                title={t('timeline.documentTooltip', {
                  name: doc.fileName,
                  date: formatDay(doc.parsedDate),
                })}
              >
                {/* Semi-transparent so stacked same-day documents read darker. */}
                <circle
                  cx={x(doc.parsedDate.getTime())}
                  cy={BASELINE_Y}
                  r={6}
                  fill={theme.palette.primary.main}
                  fillOpacity={0.45}
                />
              </Tooltip>
            ))}
          </svg>
        ) : null}
      </Box>

      {/* Add / edit milestone dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? t('timeline.editMilestone') : t('timeline.addMilestone')}</DialogTitle>
        <form onSubmit={handleSubmit(handleSave)}>
          <DialogContent>
            {/* Day-first mask in both languages, matching the demographics date field: en-GB stands in for English. */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale ?? enGB}>
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Controller
                  name="date"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (v instanceof Date && isValid(v)) || 'timeline.pickDate',
                  }}
                  render={({ field, fieldState: { error: fieldError } }) => (
                    <DatePicker
                      value={field.value}
                      onChange={(date) => field.onChange(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          label: t('timeline.date'),
                          error: !!fieldError,
                          helperText: fieldError?.message && t(fieldError.message),
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="text"
                  control={control}
                  rules={{
                    required: 'timeline.sayWhatHappened',
                    maxLength: { value: 120, message: 'timeline.maxLength' },
                  }}
                  render={({ field, fieldState: { error: fieldError } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('timeline.whatHappened')}
                      placeholder={t('timeline.whatHappenedPlaceholder')}
                      error={!!fieldError}
                      helperText={fieldError?.message && t(fieldError.message)}
                    />
                  )}
                />
              </Stack>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            {editing && (
              <Button
                color="error"
                onClick={() => setPendingDelete(editing)}
                disabled={saving}
                sx={{ mr: 'auto' }}
              >
                {t('common.remove')}
              </Button>
            )}
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <LoadingButton type="submit" variant="contained" loading={saving}>
              {editing ? t('common.save') : t('timeline.addMilestoneButton')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        title={t('timeline.removeTitle')}
        message={
          pendingDelete
            ? t('timeline.removeMessage', { text: pendingDelete.text, date: formatDay(pendingDelete.parsedDate) })
            : ''
        }
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </Paper>
  );
};

export default DocumentsTimeline;
